import React, { useState, useEffect } from 'react';
import { petData } from '../utils/constants';

function AddPetModal({ isOpen, onClose, onSave, initialPetData, loading }) {
    const [petName, setPetName] = useState('');
    const [petType, setPetType] = useState('');
    const [petBreed, setPetBreed] = useState('');
    const [petGender, setPetGender] = useState('');
    const [petDOB, setPetDOB] = useState('');
    const [petColor, setPetColor] = useState('');
    const [petMicrochipProvider, setPetMicrochipProvider] = useState('');
    const [petMicrochipNumber, setPetMicrochipNumber] = useState('');
    const [petSpecialNeeds, setPetSpecialNeeds] = useState('');
    const [petPictureFile, setPetPictureFile] = useState(null);
    const [petPictureUrl, setPetPictureUrl] = useState('');
    const [petDocumentFiles, setPetDocumentFiles] = useState([]);
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [breeds, setBreeds] = useState([]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        if (initialPetData) {
            setPetName(initialPetData.name || '');
            setPetType(initialPetData.type || '');
            setPetBreed(initialPetData.breed || '');
            setPetGender(initialPetData.gender || '');
            setPetDOB(initialPetData.dob || '');
            setPetColor(initialPetData.color || '');
            setPetMicrochipProvider(initialPetData.microchipProvider || '');
            setPetMicrochipNumber(initialPetData.microchipNumber || '');
            setPetSpecialNeeds(initialPetData.specialNeeds || '');
            setPetPictureUrl(initialPetData.pictureUrl || '');
            setExistingDocuments(initialPetData.documents || []);
            setPetPictureFile(null);
            setPetDocumentFiles([]);
            if(initialPetData.type) {
                setBreeds(petData[initialPetData.type] || []);
            }
        } else {
            setPetName('');
            setPetType('');
            setPetBreed('');
            setPetGender('');
            setPetDOB('');
            setPetColor('');
            setPetMicrochipProvider('');
            setPetMicrochipNumber('');
            setPetSpecialNeeds('');
            setPetPictureFile(null);
            setPetPictureUrl('');
            setExistingDocuments([]);
            setPetDocumentFiles([]);
            setBreeds([]);
        }
    }, [initialPetData]);
    
    const handleTypeChange = (e) => {
        const type = e.target.value;
        setPetType(type);
        setPetBreed('');
        setBreeds(petData[type] || []);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const petInfo = {
            name: petName,
            type: petType,
            breed: petBreed,
            gender: petGender,
            dob: petDOB,
            color: petColor,
            microchipProvider: petMicrochipProvider,
            microchipNumber: petMicrochipNumber,
            specialNeeds: petSpecialNeeds,
            pictureUrl: petPictureUrl,
            pictureFile: petPictureFile,
            documents: existingDocuments,
            newDocuments: Array.from(petDocumentFiles)
        };
        onSave(petInfo);
    };

    const handleDocumentRemove = (docUrlToRemove) => {
        setExistingDocuments(prev => prev.filter(doc => doc.url !== docUrlToRemove));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto p-4 flex justify-center pt-12">
            <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md relative my-auto">
                <h3 className="text-2xl font-bold text-gray-800 mb-5 text-center">
                    {initialPetData ? `Edit ${initialPetData.name}` : 'Add New Pet'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* ... (Keeping form content same as original) */}
                    <div>
                        <label htmlFor="modalPetName" className="block text-sm font-medium text-gray-700 mb-1">Pet Name</label>
                        <input type="text" id="modalPetName" value={petName} onChange={(e) => setPetName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="Buddy" required />
                    </div>
                    <div>
                        <label htmlFor="modalPetType" className="block text-sm font-medium text-gray-700 mb-1">Pet Type</label>
                        <select id="modalPetType" value={petType} onChange={handleTypeChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white" required>
                            <option value="">Select Type</option>
                            {Object.keys(petData).sort((a, b) => {
                                if (a === 'Other') return 1;
                                if (b === 'Other') return -1;
                                return a.localeCompare(b);
                            }).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    {petType && (
                        <div>
                            <label htmlFor="modalPetBreed" className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
                            <select id="modalPetBreed" value={petBreed} onChange={(e) => setPetBreed(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white" required>
                                <option value="">Select Breed</option>
                                {breeds.map(breed => (
                                    <option key={breed} value={breed}>{breed}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label htmlFor="modalPetGender" className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                        <select id="modalPetGender" value={petGender} onChange={(e) => setPetGender(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white" required>
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="modalPetDOB" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                        <input type="date" id="modalPetDOB" value={petDOB} onChange={(e) => setPetDOB(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" required />
                    </div>
                    <div>
                        <label htmlFor="modalPetColor" className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                        <input type="text" id="modalPetColor" value={petColor} onChange={(e) => setPetColor(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="Golden" required />
                    </div>
                    <div>
                        <label htmlFor="modalPetMicrochipProvider" className="block text-sm font-medium text-gray-700 mb-1">Microchip Provider</label>
                        <input type="text" id="modalPetMicrochipProvider" value={petMicrochipProvider} onChange={(e) => setPetMicrochipProvider(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., AVID" />
                    </div>
                     <div>
                        <label htmlFor="modalPetMicrochipNumber" className="block text-sm font-medium text-gray-700 mb-1">Microchip Number</label>
                        <input type="text" id="modalPetMicrochipNumber" value={petMicrochipNumber} onChange={(e) => setPetMicrochipNumber(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., 990000000000000" />
                    </div>
                    <div>
                        <label htmlFor="modalPetSpecialNeeds" className="block text-sm font-medium text-gray-700 mb-1">Special Needs</label>
                        <textarea id="modalPetSpecialNeeds" value={petSpecialNeeds} onChange={(e) => setPetSpecialNeeds(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., requires a specific diet" rows="3" />
                    </div>
                    <div>
                        <label htmlFor="modalPetPicture" className="block text-sm font-medium text-gray-700 mb-1">Pet Picture</label>
                        {petPictureUrl && !petPictureFile && (
                            <p className="text-sm text-gray-500 mb-2">Current: <a href={petPictureUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">View Image</a></p>
                        )}
                        <input type="file" id="modalPetPicture" accept="image/*" onChange={(e) => setPetPictureFile(e.target.files[0])} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                    </div>
                    <div>
                        <label htmlFor="modalPetDocuments" className="block text-sm font-medium text-gray-700 mb-1">Pet Documents</label>
                        {existingDocuments.length > 0 && (
                            <div className="text-sm text-gray-500 mb-2">
                                <p>Existing Documents:</p>
                                <ul className="list-disc list-inside">
                                    {existingDocuments.map((doc, index) => (
                                        <li key={index} className="flex items-center justify-between truncate">
                                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                                {doc.name}
                                            </a>
                                            <button type="button" onClick={() => handleDocumentRemove(doc.url)} className="ml-2 text-red-500 hover:text-red-700">x</button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <input type="file" id="modalPetDocuments" accept=".pdf,.doc,.docx" multiple onChange={(e) => setPetDocumentFiles(e.target.files)} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                    </div>
                    <div className="flex space-x-4 mt-6">
                        <button type="submit" disabled={loading} className="flex-1 py-3 px-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition duration-200 disabled:opacity-50">
                            {loading ? 'Saving...' : (initialPetData ? 'Save Changes' : 'Add Pet')}
                        </button>
                        <button type="button" onClick={onClose} className="flex-1 py-3 px-4 bg-gray-300 text-gray-800 font-bold rounded-lg hover:bg-gray-400 transition duration-200 disabled:opacity-50">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddPetModal;
